/**
 * Merge-tag helpers shared by touchpoint / Review Ask editors.
 * Matches the same tokens the senders substitute at runtime.
 */

/** Same loose spacing tolerance as jobs/touchpoint-sender.js */
export const CONTACT_NAME_TOKEN_RE =
  /\{\{\s*contact\s*\.\s*(?:first_)?name\s*\}\}/gi;

export const GMB_URL_TOKEN_RE = /\{\{\s*gmb_url\s*\}\}/gi;

/** Near-misses that look like name merges but will NOT be substituted */
export const NEAR_MISS_NAME_RE =
  /(?:\{first_name\}|\{\{\s*first_name\s*\}\}|\{\{\s*name\s*\}\}|\{name\})/gi;

/**
 * @param {string} text
 * @returns {boolean}
 */
export function hasValidNameMerge(text) {
  if (!text) return false;
  CONTACT_NAME_TOKEN_RE.lastIndex = 0;
  return CONTACT_NAME_TOKEN_RE.test(text);
}

/**
 * @param {string} text
 * @returns {boolean}
 */
export function hasNearMissNameMerge(text) {
  if (!text) return false;
  NEAR_MISS_NAME_RE.lastIndex = 0;
  return NEAR_MISS_NAME_RE.test(text);
}

/**
 * Split message into plain + token segments for chip rendering.
 * @param {string} text
 * @param {{ includeGmb?: boolean }} [opts]
 * @returns {Array<{ type: 'text'|'token', value: string, kind?: 'name'|'gmb' }>}
 */
export function splitMergeTagSegments(text, opts = {}) {
  const source = text || '';
  if (!source) return [{ type: 'text', value: '' }];

  const pattern = opts.includeGmb
    ? /(\{\{\s*contact\s*\.\s*(?:first_)?name\s*\}\}|\{\{\s*gmb_url\s*\}\})/gi
    : /(\{\{\s*contact\s*\.\s*(?:first_)?name\s*\}\})/gi;

  const parts = source.split(pattern);
  return parts
    .filter((p) => p !== undefined && p !== '')
    .map((part) => {
      CONTACT_NAME_TOKEN_RE.lastIndex = 0;
      GMB_URL_TOKEN_RE.lastIndex = 0;
      if (CONTACT_NAME_TOKEN_RE.test(part)) {
        return { type: 'token', value: part, kind: 'name' };
      }
      if (opts.includeGmb && GMB_URL_TOKEN_RE.test(part)) {
        return { type: 'token', value: part, kind: 'gmb' };
      }
      return { type: 'text', value: part };
    });
}

/**
 * Preview personalization for UI (sample first name).
 * @param {string} text
 * @param {{ firstName?: string, gmbUrl?: string }} [vars]
 */
export function personalizeForPreview(text, vars = {}) {
  const firstName = vars.firstName || 'there';
  const gmbUrl = vars.gmbUrl || '';
  return String(text || '')
    .replace(CONTACT_NAME_TOKEN_RE, firstName)
    .replace(GMB_URL_TOKEN_RE, gmbUrl);
}

/**
 * Insert a token at the textarea selection (or append).
 * @param {HTMLTextAreaElement|null} el
 * @param {string} current
 * @param {string} token
 * @returns {{ next: string, selectionStart: number }}
 */
export function insertTokenAtCursor(el, current, token) {
  const value = current || '';
  if (!el) {
    const next = value + token;
    return { next, selectionStart: next.length };
  }
  const start = el.selectionStart ?? value.length;
  const end = el.selectionEnd ?? value.length;
  const next = value.slice(0, start) + token + value.slice(end);
  return { next, selectionStart: start + token.length };
}
